'use client';
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessageBot, faUser, faArrowUp } from '@fortawesome/pro-duotone-svg-icons';
import Lottie from 'react-lottie-player';
import Markdown from 'react-markdown';
import Modal from '@/components/common/Modal';

import * as loadingAnimation from './loadingAnimation.json';
import { startChat, postMessage, getChatMessages } from '@/api/requests/chats';
import CustomParagraph from './CustomParagraph';
import { aiQuestions } from '@/data/aiQuestions';
import log from '@/utils/logger';
import useWindowWidth from '@/hooks/useWindowWidth';
import { Chat, ChatMessage } from '@/actions/dtos/chat';
import { useAccount } from '@/hooks/useAccount';

interface AiChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenderId: string;
  chatId: string;
  setChatId: (id: string) => void;
  isLimitReached: boolean;
  refreshChats: () => void;
}

const AiChatModal: React.FC<AiChatModalProps> = ({
  isOpen,
  onClose,
  tenderId,
  chatId,
  setChatId,
  isLimitReached,
  refreshChats,
}) => {
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [randomQuestions, setRandomQuestions] = useState<string[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const windowWidth = useWindowWidth();
  const { refreshCustomer } = useAccount();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
  };

  const fetchChatMessages = async (chatId: string) => {
    try {
      setLoadingMessages(true);
      const messages = await getChatMessages(chatId);
      setChatMessages(messages);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSubmit = async (text: string) => {
    if (!text.trim()) return;

    const userMessage: ChatMessage = {
      type: 'HUMAN',
      text: text,
      created: new Date(),
      annotations: [],
      id: '',
      version: 0,
      updated: new Date(),
    };
    setChatMessages([...chatMessages, userMessage]);
    setInputText('');
    setLoading(true);
    try {
      let response: Chat;
      if (!chatId) {
        response = await startChat(tenderId, text);
        setChatId(response.id);
        await fetchChatMessages(response.id);
        refreshChats();
      } else {
        response = await postMessage(chatId, text);
        await fetchChatMessages(chatId);
      }
      refreshCustomer();
    } catch (error) {
      log.error('Error fetching chatbot response:', error);
      const fallbackMessage: ChatMessage = {
        type: 'ASSISTANT',
        text: 'Leider konnte ich die Information nicht finden.',
        created: new Date(),
        annotations: [],
        id: '',
        version: 0,
        updated: new Date(),
      };
      setChatMessages([...chatMessages, userMessage, fallbackMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputText.trim() && !loading && !isLimitReached) {
      handleSubmit(inputText);
    }
  };

  const handleCloseClick = () => {
    setChatId('');
    setChatMessages([]);
    onClose();
  };

  const handleExample = (text: string) => {
    setInputText(text);
    handleSubmit(text);
  };

  useEffect(() => {
    if (isOpen) {
      const shuffled = [...aiQuestions].sort(() => 0.5 - Math.random());
      const numberOfQuestions = windowWidth < 560 ? 2 : 4;
      setRandomQuestions(shuffled.slice(0, numberOfQuestions));
    }
  }, [isOpen, windowWidth]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatMessages]);

  useEffect(() => {
    if (chatId && isOpen) {
      fetchChatMessages(chatId);
    } else if (!chatId) {
      setChatMessages([]);
      setLoadingMessages(false);
    }
  }, [chatId, isOpen]);

  const renderActions = () => (
    <div className="card flex w-full flex-row gap-3 overflow-ellipsis rounded-full bg-base-200 px-3 py-2">
      <input
        type="text"
        className="grow overflow-ellipsis bg-base-200 focus:outline-none"
        placeholder={isLimitReached ? 'Limit erreicht. Upgrade für mehr Fragen!' : 'Hier Ihre Frage eingeben …'}
        value={inputText}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        disabled={isLimitReached}
        maxLength={300}
      />
      <button
        className="h-6 w-6 rounded-full hover:bg-primary hover:text-primary-content"
        onClick={() => handleSubmit(inputText)}
        disabled={loading}
      >
        <FontAwesomeIcon icon={faArrowUp} className="h-4 justify-self-center" />
      </button>
    </div>
  );

  const showSuggestions = chatMessages.length === 0 && !loadingMessages && !chatId;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseClick}
      title={'KI-Assistent'}
      actions={renderActions()}
      contentRef={scrollRef}
    >
      <div className="mt-4 flex h-full min-h-0 flex-grow flex-col justify-end xl:w-4xl">
        {showSuggestions ? (
          <div className="flex h-full flex-col justify-between">
            <div className="h-full place-content-center max-md:py-4">
              <div className="grid grid-cols-2 gap-4 px-14 max-xl:px-4 max-sm:grid-cols-1">
                {randomQuestions.map((question, index) => (
                  <button
                    onClick={() => handleExample(question)}
                    className="card rounded-lg bg-base-200 p-3 px-1 hover:underline md:p-6"
                    key={index}
                  >
                    <FontAwesomeIcon icon={faMessageBot} className="h-6 w-full justify-self-center" />
                    <p className="w-full pt-2 text-center text-sm">{question}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="card my-4 mr-20 flex flex-row gap-3 rounded-lg bg-base-200 px-3 py-2 max-sm:mr-6">
              <FontAwesomeIcon icon={faMessageBot} className="h-4 w-4 justify-self-center pt-1" />
              <p className="max-sm:text-sm">
                Hallo, ich bin Ihre KI-Beratung. Haben Sie Fragen zur Ausschreibung? Ich antworte gerne basierend auf
                den bereitgestellten Dokumenten.
              </p>
            </div>
          </div>
        ) : (
          <div className="mb-4 flex h-full w-full flex-col flex-nowrap rounded-xl">
            {chatMessages.map((message, index) => (
              <div
                key={index}
                className={` ${message.type === 'HUMAN' ? 'ml-24 flex justify-end max-sm:ml-6' : 'mr-24 max-sm:mr-6'} my-2 rounded-lg p-2`}
              >
                <div
                  key={index}
                  className={`message flex space-x-2 ${
                    message.type === 'HUMAN' ? 'w-fit self-end bg-base-200 text-right' : 'w-fit bg-base-200 text-left'
                  } my-2 rounded-lg p-2`}
                >
                  {message.type === 'ASSISTANT' && (
                    <FontAwesomeIcon icon={faMessageBot} className="h-5 w-5 justify-self-center pt-1" />
                  )}
                  <Markdown
                    className="flex flex-col"
                    components={{
                      p: (props) => <CustomParagraph as="p" {...props} index={index} chatMessages={chatMessages} />,
                      li: (props) => <CustomParagraph as="li" {...props} index={index} chatMessages={chatMessages} />,
                      ul: ({ children, ...props }) => (
                        <ul className="list-disc pl-6" {...props}>
                          {children}
                        </ul>
                      ),
                      ol: ({ children, ...props }) => (
                        <ol className="list-decimal pl-6" {...props}>
                          {children}
                        </ol>
                      ),
                    }}
                  >
                    {message.text}
                  </Markdown>
                  {message.type === 'HUMAN' && (
                    <FontAwesomeIcon icon={faUser} className="h-4 w-4 justify-self-center pt-1" />
                  )}
                </div>
              </div>
            ))}
            {(loading || loadingMessages) && (
              <div className="flex h-full w-full flex-col items-center justify-center">
                {loading ? (
                  <>
                    <Lottie loop animationData={loadingAnimation} play style={{ width: 150, height: 50 }} />
                    <p className="text-center text-sm font-light text-dark-gray-blue">KI verarbeitet die Anfrage</p>
                  </>
                ) : (
                  <span className="loading loading-spinner" />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AiChatModal;
